/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IModuleProps, INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { createAccordionStateInput } from '../../../data-actions/accordion/accordion-state-data-action';
import { IAccordionItemProps } from '../definition-extensions/accordion-item.ext.props.autogenerated';
import { IAccordionItemData } from './accordion-item.data';
import Drawer, { IDrawerState } from './components/citta-drawer';

export const enum interactionStyle {
  Independent = 'Independent',
  ExpandOneItemOnly = 'ExpandOneItemOnly'
}

export interface IControlledDrawerState {
  isOpen: boolean;
  accordionText?: string;
}

export interface ICittaAccordionItemViewProps extends IAccordionItemProps<IAccordionItemData> {
  AccordionItemContainer: IModuleProps;
  drawer: INodeProps;
  accordionSlots: INodeProps;
  AccordionItemContent: INodeProps;
  className: string;
  interactionStyle: interactionStyle;
}

const AccordionItemView: React.FC<ICittaAccordionItemViewProps> = (props) => {
  const { data, config, drawer, accordionSlots, AccordionItemContainer, AccordionItemContent } = props;
  const accordionExpandedStateResult = data && data.accordionExpandedState && data.accordionExpandedState.result;
  const expanded = accordionExpandedStateResult && accordionExpandedStateResult.isAllExpanded === undefined ?
    data && config.isExpandedOnInitialLoad :
    (accordionExpandedStateResult && accordionExpandedStateResult.isAllExpanded);

  const [state] = React.useState<IDrawerState>({
    isOpen: false
  });

  let toggleButtonText = drawer.toggleButtonText;
  if (toggleButtonText.indexOf('Product dimensions') != -1) {
    toggleButtonText = 'Product Details';
  } 

  const drawerProps = {
    tag: drawer.tag,
    className: drawer.className,
    toggleButtonText: toggleButtonText,
    glyphPlacement: config.glyphPlacement,
    radioButtonOption: config.radioButtonOption,
    collapseProps: { isOpen: expanded },
    openGlyph: 'expand-glyph',
    closeGlyph: 'collapse-glyph',
    selectedStateOptionText: accordionExpandedStateResult?.radioButtonText,
    onToggle: () => {
      _onToggle(props, toggleButtonText, state);
    }
  };

  // PRODUCT DATA LOADING
  let content: IAccordionItemData;

  try {
    content = data;
  } catch (e) {
    return <div>{e}</div>;
  }

  let accordionItemContent = getAccordionItemContent(content, config.accordionItemTitle);
  let hasAccordionItemContent = accordionItemContent.length > 0;
  const hasAccordionItemSlotsContent = accordionSlots && accordionSlots.accordionItemContent && accordionSlots.accordionItemContent.length;
  return (
    <>
      {hasAccordionItemContent && (
        <Module {...AccordionItemContainer} >
          <div className='ms-accordion-item-title'>
            <Drawer
              {...drawerProps}
              toggleButtonTextClick={false}
            >
              <Node {...AccordionItemContent}>
                {hasAccordionItemContent && (renderAccordionItemContent(accordionItemContent))}
                {hasAccordionItemSlotsContent && (renderAccordionItemSlotsContent(accordionSlots.accordionItemContent))}
              </Node>
            </Drawer>
          </div>
        </Module>
      )}

      {!hasAccordionItemContent && hasAccordionItemSlotsContent && (
        <Module {...AccordionItemContainer} >
          <div className='ms-accordion-item-title'>
            <Drawer
              {...drawerProps}
              toggleButtonTextClick={false}
            >
              <Node {...AccordionItemContent}>
                {hasAccordionItemContent && (renderAccordionItemContent(accordionItemContent))}
                {hasAccordionItemSlotsContent && (renderAccordionItemSlotsContent(accordionSlots.accordionItemContent))}
              </Node>
            </Drawer>
          </div>
        </Module>
      )}
    </>
  );
};

const getAccordionItemContent = (content: IAccordionItemData, accordionItemTitle: string): any[] =>  {
  let titles = accordionItemTitle.split('|');
  let returnVal: any[] = [];
  titles.forEach(t => {

    if (t.toLocaleLowerCase() === 'care instructions') {
      returnVal.push(<h6>{t}:</h6>);
    }


    const productSpec = content.productSpecificationData.result;
    let contentText = '';

    if (productSpec) {
      productSpec.some(spec => {
        if (spec.TextValue && spec.Name === t) {
          contentText = spec.TextValue;
          return true;
        }
        return false;
      });
    }

    
    if (contentText && t.toLocaleLowerCase() === 'additional information') {
      returnVal.push(<h6>{t}:</h6>);
    }
    
    returnVal.push(<p>{contentText}</p>);

  })
  return returnVal;
};

const renderAccordionItemContent = (content: any[]): JSX.Element | null => {
  return (
    <div className='container'>
      {content.map(c => {
        return (<span>{c}</span>)
      })}
    </div>
  );
};

const renderAccordionItemSlotsContent = (items: React.ReactNode[]): JSX.Element | null => {
  return (
    <>
      {items && items.length ? (
        items.map((item: React.ReactNode, index: number) => {
          return (
            <React.Fragment key={index}>
              {item}
            </React.Fragment>
          );
        })
      ) : null}
    </>
  );
};

const _onToggle = (props: ICittaAccordionItemViewProps, toggleButtonText: string, state: IDrawerState): void => {
  if (!state.isOpen && props.interactionStyle === interactionStyle.ExpandOneItemOnly) {
    const accordionState = { isAllExpanded: false, radioButtonText: toggleButtonText };
    props.context.actionContext.update(createAccordionStateInput(accordionState), accordionState);
  }
};

export default AccordionItemView;